import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, ArrowClockwise, Buildings, Handshake, Question, Warning, Sparkle, EyeSlash } from "@phosphor-icons/react";

const CLASS_META = {
  FINAL_CLIENT: { label: "Clients finaux", color: "#22C55E", icon: Buildings },
  ESN_CONSULTING: { label: "ESN / Cabinets", color: "#F59E0B", icon: Handshake },
  STAFFING_RECRUITMENT: { label: "Intermédiaires", color: "#FB7185", icon: Handshake },
  UNKNOWN: { label: "À confirmer", color: "#94A3B8", icon: Question },
};

function Stat({ label, value, color, icon: Icon }) {
  return (
    <div className="surface rounded-lg p-4" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={16} style={{ color: color || "#00E5FF" }} />}
        <span className="overline !text-muted">{label}</span>
      </div>
      <div className="font-display text-2xl font-bold" style={{ color: color || "#fff" }}>{value}</div>
    </div>
  );
}

export default function AdminClassification() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/classification/quality");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reclassify = async (force) => {
    setBusy(true);
    try {
      await api.post("/admin/classification/reclassify", { force, allow_llm: true });
      toast.success(force ? "Reclassification complète lancée (arrière-plan)" : "Classification des entreprises restantes lancée");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loading || !data) return <div className="surface rounded-lg p-10 text-center text-muted" data-testid="classification-loading">Chargement…</div>;

  const by = data.by_class || {};
  return (
    <div data-testid="admin-classification-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={22} className="text-accent" weight="duotone" />
            <h1 className="font-display text-2xl font-bold">Qualité de la classification</h1>
          </div>
          <p className="text-muted text-sm max-w-2xl">Répartition des entreprises par catégorie, cas à confirmer, faible confiance et corrections manuelles. La correction manuelle prime toujours sur la classification automatique.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => reclassify(false)} disabled={busy} data-testid="reclassify-remaining-btn"
            className="flex items-center gap-2 text-sm border border-border rounded-md px-4 py-2.5 hover-lift hover:border-accent/40 text-fg disabled:opacity-60">
            <ArrowClockwise size={16} /> Classer les restantes
          </button>
          <button onClick={() => reclassify(true)} disabled={busy} data-testid="reclassify-all-btn"
            className="flex items-center gap-2 text-sm bg-accent/15 text-accent border border-accent/30 rounded-md px-4 py-2.5 hover-lift disabled:opacity-60">
            <ArrowClockwise size={16} /> Tout reclassifier
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="class-distribution">
        {["FINAL_CLIENT", "ESN_CONSULTING", "STAFFING_RECRUITMENT", "UNKNOWN"].map((k) => {
          const m = CLASS_META[k];
          return <Stat key={k} label={m.label} value={by[k] ?? 0} color={m.color} icon={m.icon} />;
        })}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat label="Total entreprises" value={data.total} />
        <Stat label="Migrées" value={`${data.migrated}/${data.total}`} color={data.migration_done ? "#22C55E" : "#F59E0B"} />
        <Stat label="Faible confiance" value={data.low_confidence} color="#F59E0B" icon={Warning} />
        <Stat label="Corrections manuelles" value={data.manual_corrections} color="#00E5FF" />
        <Stat label="Classées par IA" value={data.llm_classified} color="#7C5CFF" icon={Sparkle} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="surface rounded-lg p-5" data-testid="recent-searches-panel">
          <div className="overline mb-3">Recherches récentes — clients finaux vs ESN</div>
          {(data.recent_searches || []).length === 0 ? (
            <div className="text-sm text-muted">Aucune recherche enregistrée.</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.recent_searches.map((s, i) => (
                <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-border/40 pb-2">
                  <span className="text-fg/90 truncate flex-1">{s.query}</span>
                  <span className="font-mono text-xs flex items-center gap-2 shrink-0">
                    <span className="text-[#22C55E]">{s.final} finaux</span>
                    <span className="text-[#F59E0B]">{s.esn} ESN</span>
                    {s.unknown ? <span className="text-muted">{s.unknown} ?</span> : null}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="surface rounded-lg p-5" data-testid="top-hidden-panel">
          <div className="overline mb-3 flex items-center gap-2"><EyeSlash size={13} /> Entreprises les plus masquées par les clients</div>
          {(data.top_hidden || []).length === 0 ? (
            <div className="text-sm text-muted">Aucune entreprise masquée pour le moment.</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.top_hidden.map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-border/40 pb-2">
                  <button onClick={() => navigate(`/app/company/${h.company_id}`)} className="text-fg/90 truncate flex-1 text-left hover:text-accent">{h.name || h.company_id}</button>
                  <span className="font-mono text-xs text-rose-300 shrink-0">{h.hidden_by} org.</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <button onClick={() => navigate("/admin/content")} data-testid="goto-unknown-companies"
          className="text-sm text-accent hover:underline">Voir et corriger les entreprises « À confirmer » dans Contenu marché →</button>
      </div>
    </div>
  );
}
